import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import services from '../data/services';
import SectionHeader from '../components/SectionHeader';
import '../styles/pages/home.css';

gsap.registerPlugin(ScrollTrigger);

const DEMO_URL = 'https://calendar.app.google/6g62S4xtRa8eWipt5';

/* The six sticky notes from the hero photo, walked through one by one. */
const AUTOMATIONS = [
  { note: <>Phone<br />answering</>, title: 'Phone answering', icon: 'fa-phone-volume',
    text: 'A 24/7 AI voice assistant answers calls and SMS, so no caller waits on hold or lands in voicemail.' },
  { note: <>Visitor / Staff<br />check-in</>, title: 'Visitor & staff check-in', icon: 'fa-id-badge',
    text: 'A kiosk manages visitor and staff check-ins, with everything tracked automatically.' },
  { note: <>AI team<br />assistant</>, title: 'AI team assistant', icon: 'fa-robot',
    text: 'A private assistant answers your team\u2019s repeat questions from your own documents, with sources you can verify.' },
  { note: <>Contacts &amp;<br />CRM</>, title: 'Contacts & CRM', icon: 'fa-address-book',
    text: 'Details from calls, chats and forms flow straight into your records instead of being re-typed by hand.' },
  { note: <><span className="nw">Appointments /</span><br />reminders</>, title: 'Appointments & reminders', icon: 'fa-calendar-check',
    text: 'Intelligent scheduling plus reminder sequences with confirmation links to cut no-shows.' },
  { note: <>Integrations</>, title: 'Integrations', icon: 'fa-plug',
    text: 'Connects to Power Platform, Microsoft 365, Teams and SharePoint, EMR/EHR systems and major cloud platforms.' },
];

const STATS = [
  { n: 20, suffix: '+', label: 'Hours saved per week with AI automation' },
  { n: 24, suffix: '/7', label: 'Digital receptionist, always answering' },
  { n: 50, suffix: '+', label: 'Projects delivered' },
  { n: 1000, suffix: '+', label: 'Hours saved weekly across our clients' },
];

const INDUSTRIES = ['Clinics', 'Law firms', 'Property managers', 'Public agencies', 'Education', 'Government', 'Healthcare'];

const STEPS = [
  { step: '01', title: 'Discover', icon: 'fa-magnifying-glass',
    desc: 'We map your current workflow, bottlenecks, and constraints in a focused discovery sprint. Then we define a clear automation scope with measurable outcomes.' },
  { step: '02', title: 'Build', icon: 'fa-code',
    desc: 'We ship a production-ready solution using secure integrations and rapid feedback loops. You see working increments early, not just slide decks.' },
  { step: '03', title: 'Deploy & support', icon: 'fa-rocket',
    desc: 'We launch with team enablement, monitor adoption, and tune continuously. Ongoing support keeps performance high as your operations evolve.' },
];

const SECURITY = [
  { icon: 'fa-flag', title: 'Canadian hosting', text: 'Canada hosting options for regulated workflows.' },
  { icon: 'fa-lock', title: 'Encryption', text: 'Encryption in transit and at rest.' },
  { icon: 'fa-user-shield', title: 'Access control', text: 'Role-based access control and least-privilege permissions.' },
  { icon: 'fa-clipboard-list', title: 'Audit logs', text: 'Audit logs for key actions and access events.' },
  { icon: 'fa-database', title: 'Data retention', text: 'Defined data-retention policies aligned with business and compliance needs.' },
];

const PRODUCTS = [
  { icon: 'fa-screwdriver-wrench', title: 'TextGenetic', subtitle: 'co-founded by Dr. Esmali',
    desc: 'Predictive maintenance & data-integration platform that analyzes vehicle and sensor data to forecast equipment failures and optimize fleet health.',
    link: 'https://textgenetic.com', linkText: 'Visit Site', external: true },
  { icon: 'fa-file-waveform', title: 'My Clinical Writer', subtitle: 'co-founded by Dr. Esmali',
    desc: 'AI-assisted documentation for psychologists and mental-health professionals \u2014 streamlines SOAP notes and reports while preserving patient privacy.',
    link: 'https://myclinicalwriter.ai', linkText: 'Visit Site', external: true },
  { icon: services.cryptocurrency.icon, title: services.cryptocurrency.title, subtitle: services.cryptocurrency.subtitle,
    desc: services.cryptocurrency.shortDescription,
    link: `/services/${services.cryptocurrency.id}`, linkText: 'Learn More', external: false },
];

/* Scroll-driven walkthrough: desktop only, never with reduced motion.
   Without it every automation is simply shown in full. */
function useAutomationScroll(ref) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || typeof window === 'undefined' || !window.matchMedia) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let mm;
    const ctx = gsap.context(() => {
      mm = gsap.matchMedia();
      mm.add('(min-width: 992px) and (min-height: 640px)', () => {
        const pin = root.querySelector('.home-auto__pin');
        const items = Array.from(root.querySelectorAll('[data-auto-step]'));
        const notes = Array.from(root.querySelectorAll('[data-auto-note]'));
        const count = root.querySelector('.home-auto__count-now');
        let current = -1;
        const setActive = (i) => {
          if (i === current) return;
          current = i;
          items.forEach((el, k) => el.classList.toggle('is-active', k === i));
          notes.forEach((el, k) => el.classList.toggle('is-active', k === i));
          if (count) count.textContent = String(i + 1).padStart(2, '0');
        };
        root.classList.add('is-scrolly');
        setActive(0);
        ScrollTrigger.create({
          trigger: pin,
          pin: true,
          start: 'top top',
          end: () => `+=${Math.round(window.innerHeight * 2.6)}`,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            pin.style.setProperty('--progress', self.progress.toFixed(4));
            setActive(Math.min(AUTOMATIONS.length - 1, Math.floor(self.progress * AUTOMATIONS.length)));
          },
        });
        return () => {
          root.classList.remove('is-scrolly');
          items.concat(notes).forEach((el) => el.classList.remove('is-active'));
          pin.style.removeProperty('--progress');
        };
      });
    }, root);
    return () => { if (mm) mm.revert(); ctx.revert(); };
  }, [ref]);
}

function Home() {
 const autoRef = useRef(null);
 useAutomationScroll(autoRef);

 return (
 <>
 {/* HERO — full-bleed photo */}
 <header id="home" className="home-hero" data-nav-over aria-labelledby="home-hero-title">
 <div className="home-hero__media" data-parallax="-0.16">
 <div className="home-hero__zoom">
 <picture>
 <source
 type="image/webp"
 srcSet="/images/hero-desk-960.webp 960w, /images/hero-desk.webp 1925w"
 sizes="(max-aspect-ratio: 1925/817) calc(100vh * 2.36), 100vw"
 />
 <img
 src="/images/hero-desk.jpg"
 width="1925"
 height="817"
 alt="A sunlit office desk with a laptop showing sticky notes for phone answering, visitor and staff check-in, AI team assistant, contacts and CRM, appointments and reminders, and integrations"
 fetchpriority="high"
 decoding="async"
 data-kenburns
 />
 </picture>
 </div>
 </div>
 <div className="home-hero__scrim" aria-hidden="true"></div>

 <div className="container home-hero__content">
 <div className="home-hero__copy" data-fade-out="70">
 <span className="pill pill--glass" data-reveal data-delay="0.2">
 <i className="fa-solid fa-bolt" aria-hidden="true"></i>
 AI-Powered Workflow Automation
 </span>
 <h1 className="home-hero__title" id="home-hero-title" data-split data-delay="0.35">
 We <span className="gradient-text">automate</span> your<br />
 most{' '}
 <span className="scribble">
 tedious
 <svg className="scribble__svg" viewBox="0 0 200 20" preserveAspectRatio="none" aria-hidden="true" focusable="false">
 <path d="M3 14 C 40 6, 90 4, 197 9 M 20 17 C 70 12, 130 11, 180 14" />
 </svg>
 </span>{' '}
 workflows
 </h1>
 <p className="home-hero__lead" data-reveal data-delay="0.75">
 Save 20+ hours per week with AI solutions that handle calls,
 streamline data, and automate repetitive tasks — while
 keeping your data private and on Canadian soil.
 </p>
 <div className="home-hero__actions" data-reveal data-delay="0.9">
 <a
 href="https://calendar.app.google/6g62S4xtRa8eWipt5"
 target="_blank"
 rel="noopener noreferrer"
 className="btn btn-primary btn-lg"
 data-magnetic
 >
 <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
 Book a Demo
 </a>
 <Link to="/solutions" className="btn btn-glass btn-lg">
 See Solutions
 <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
 </Link>
 </div>
 <ul className="ticks ticks--inline home-hero__ticks" data-reveal-stagger="0.1" data-delay="1.05">
 {['Free 30-min consultation', 'No commitment required', 'Canadian data residency'].map((item) => (
 <li key={item}>{item}</li>
 ))}
 </ul>
 </div>
 </div>

 <a href="#demo-video" className="scroll-cue">
 <span className="scroll-cue__line" aria-hidden="true"></span>
 Scroll
 </a>

 <div className="home-hero__rail">
 <div className="marquee marquee--fade" data-marquee data-speed="38" aria-label="What Moneli automates">
 <div className="marquee__track">
 {['Phone answering', 'Visitor / staff check-in', 'AI team assistant', 'Contacts & CRM', 'Appointments & reminders', 'Integrations', 'Private on-prem LLM'].map((item) => (
 <span className="marquee__item" key={item}>
 {item}
 <span className="marquee__sep" aria-hidden="true"></span>
 </span>
 ))}
 </div>
 </div>
 </div>
 </header>

 {/* TRUSTED BY */}
 <section className="section section--light section--tight home-trusted" aria-label="Trusted by and partners">
 <div className="container" data-reveal>
 <div className="trusted-strip">
 <span className="trusted-label">Trusted by / partners</span>
 <div className="trusted-logos" aria-label="Partner logos">
 <span className="partner-logo">Communitech</span>
 <span className="partner-logo">Microsoft Power Platform</span>
 <span className="partner-logo">Google Cloud</span>
 <span className="partner-logo">GitLab</span>
 </div>
 </div>
 </div>
 </section>

 {/* SIX AUTOMATIONS — scroll-driven on desktop */}
 <section id="automations" className="section section--sand section--flush home-auto" ref={autoRef} aria-labelledby="home-auto-title">
 <div className="home-auto__pin">
 <div className="container home-auto__grid">
 <div className="home-auto__copy">
 <p className="eyebrow" data-reveal><span className="eyebrow__num">01</span>What we automate</p>
 <h2 className="h2 home-auto__title" id="home-auto-title" data-split>
 Six notes, taken <em>off</em> your desk
 </h2>
 <ol className="home-auto__list" data-reveal-stagger="0.06">
 {AUTOMATIONS.map((a, i) => (
 <li className="home-auto__item" data-auto-step key={a.title}>
 <span className="home-auto__num">{String(i + 1).padStart(2, '0')}</span>
 <div className="home-auto__body">
 <h3 className="home-auto__name">
 <i className={`fa-solid ${a.icon}`} aria-hidden="true"></i>
 {a.title}
 </h3>
 <div className="home-auto__reveal">
 <p className="home-auto__text">{a.text}</p>
 </div>
 </div>
 </li>
 ))}
 </ol>
 </div>

 <div className="home-auto__stage" data-reveal="scale">
 <div className="home-auto__board" aria-hidden="true">
 <div className="home-auto__notes">
 {AUTOMATIONS.map((a, i) => (
 <div className="home-auto__note" data-auto-note key={a.title} style={{ '--i': i }}>
 <span className="home-auto__note-icon"><i className={`fa-solid ${a.icon}`}></i></span>
 <span className="home-auto__note-text">{a.note}</span>
 </div>
 ))}
 </div>
 <div className="home-auto__meter">
 <span className="home-auto__count"><span className="home-auto__count-now">01</span> / 06</span>
 <span className="home-auto__bar"><span className="home-auto__bar-fill"></span></span>
 <span className="home-auto__hint">Keep scrolling</span>
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* DEMO VIDEO + STATS */}
 <section id="demo-video" className="section section--dark grain home-demo" aria-labelledby="demo-video-title">
 <div className="container">
 <div className="feature-split">
 <div>
 <p className="eyebrow" data-reveal><span className="eyebrow__num">02</span>See it in action</p>
 <h2 className="h2" id="demo-video-title" data-split>
 Moneli Automation <em>demo</em>
 </h2>
 <p className="lead home-demo__lead" data-reveal data-delay="0.15">
 AI solutions that handle calls, streamline data, and automate repetitive tasks —
 while keeping your data private and on Canadian soil.
 </p>
 <div className="btn-group-m home-demo__actions" data-reveal data-delay="0.25">
 <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg" data-magnetic>
 Book a Demo
 </a>
 <Link to="/demos" className="btn btn-outline-light btn-lg">
 More demos
 <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
 </Link>
 </div>
 </div>
 <div className="home-demo__media" data-reveal="scale" data-delay="0.1">
 <span className="home-demo__halo" aria-hidden="true" data-parallax="-0.12"></span>
 <div className="phone" data-cursor="Watch">
 <div className="phone__screen">
 <iframe
 src="https://www.youtube.com/embed/YBBXlgjNdDE?autoplay=1&mute=1&loop=1&playlist=YBBXlgjNdDE"
 title="Moneli Automation Demo"
 loading="lazy"
 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
 allowFullScreen
 ></iframe>
 </div>
 </div>
 </div>
 </div>

 <div className="stats home-stats" data-reveal-stagger>
 {STATS.map((s) => (
 <div className="stat" key={s.label}>
 <p className="stat__num"><span data-count={s.n}>{s.n}</span><span className="stat__suffix">{s.suffix}</span></p>
 <p className="stat__label">{s.label}</p>
 </div>
 ))}
 </div>
 </div>
 </section>

 {/* INDUSTRIES MARQUEE */}
 <section className="section section--sand section--flush home-industries" aria-label="Industries we serve">
 <div className="marquee marquee--words marquee--fade" data-marquee data-speed="55">
 <div className="marquee__track">
 {INDUSTRIES.map((w, i) => (
 <span className="marquee__item" key={w}>
 {i % 2 ? <em>{w}</em> : w}
 <span className="marquee__sep" aria-hidden="true"></span>
 </span>
 ))}
 </div>
 </div>
 </section>

 {/* GOVERNMENT OF CANADA AI SOURCE LIST */}
 <section id="government-ai" className="section section--light section--tight home-gov" aria-labelledby="gov-title">
 <div className="container">
 <div className="card card--flat home-gov__card" data-reveal>
 <span className="icon-tile home-gov__icon"><i className="fa-solid fa-landmark" aria-hidden="true"></i></span>
 <div className="home-gov__body">
 <span className="badge-soft"><i className="fa-brands fa-canadian-maple-leaf" aria-hidden="true"></i> Public-sector procurement</span>
 <h2 className="home-gov__title" id="gov-title">Qualified for Government of Canada AI opportunities</h2>
 <p className="home-gov__text">
 Moneli Automation Inc. is qualified under Bands 1 and 2 of the Government of Canada Artificial Intelligence (AI) Source List. We are ready to participate in upcoming AI software procurement opportunities.
 </p>
 </div>
 </div>
 </div>
 </section>

 {/* HOW WE WORK */}
 <section id="how-we-work" className="section section--light home-process" aria-labelledby="how-title">
 <div className="container">
 <SectionHeader
 index="03"
 eyebrow="Our process"
 title={<>How we <em>work</em></>}
 lead="A practical, low-risk delivery flow that gets value live fast."
 align="split"
 id="how-title"
 />
 <div className="home-process__track">
 <span className="home-process__line" aria-hidden="true" data-reveal-line></span>
 <ol className="home-process__steps" data-reveal-stagger="0.12">
 {STEPS.map((s) => (
 <li className="home-process__step" key={s.step}>
 <div className="home-process__head">
 <span className="home-process__dot" aria-hidden="true"></span>
 <span className="home-process__num">{s.step}</span>
 </div>
 <span className="icon-tile icon-tile--sm"><i className={`fa-solid ${s.icon}`} aria-hidden="true"></i></span>
 <h3 className="home-process__title">{s.title}</h3>
 <p className="home-process__text">{s.desc}</p>
 </li>
 ))}
 </ol>
 </div>
 </div>
 </section>

 {/* SECURITY & PRIVACY */}
 <section id="security-privacy" className="section section--dark grain home-security" aria-labelledby="security-title">
 <div className="container">
 <div className="home-security__grid">
 <div className="home-security__intro">
 <p className="eyebrow" data-reveal><span className="eyebrow__num">04</span>Enterprise-grade</p>
 <h2 className="h2" id="security-title" data-split>Security &amp; <em>privacy</em></h2>
 <p className="lead" data-reveal data-delay="0.15">
 Built for organizations that need clear controls and reliable governance.
 </p>
 <div className="home-security__seal" data-reveal data-delay="0.25" aria-hidden="true">
 <i className="fa-solid fa-shield-halved"></i>
 <span className="home-security__ring"></span>
 </div>
 </div>
 <ul className="home-security__list" data-reveal-stagger="0.08">
 {SECURITY.map((s, i) => (
 <li className="home-security__row" key={s.title}>
 <span className="home-security__num">{String(i + 1).padStart(2, '0')}</span>
 <span className="icon-tile icon-tile--sm"><i className={`fa-solid ${s.icon}`} aria-hidden="true"></i></span>
 <div>
 <h3 className="home-security__title">{s.title}</h3>
 <p className="home-security__text">{s.text}</p>
 </div>
 </li>
 ))}
 </ul>
 </div>
 </div>
 </section>

 {/* PRODUCTS / ECOSYSTEM */}
 <section id="products" className="section section--light home-products" aria-labelledby="products-title">
 <div className="container">
 <SectionHeader
 index="05"
 eyebrow="Product suite"
 title={<>Our ecosystem of <em>AI-driven</em> solutions</>}
 lead={<>Our flagship platform <strong>Informed Intake</strong> reimagines client communication and automation. We also co-founded products that demonstrate our track record of turning research into scalable solutions.</>}
 align="split"
 id="products-title"
 />
 <div className="home-products__grid" data-reveal-stagger="0.1">
 <article className="card card--dark theme-dark home-products__flagship">
 <span className="home-products__glow" aria-hidden="true"></span>
 <div className="home-products__main">
 <div className="home-products__top">
 <span className="icon-tile home-products__icon"><i className="fa-solid fa-headset" aria-hidden="true"></i></span>
 <span className="pill pill--note home-products__badge"><i className="fa-solid fa-star" aria-hidden="true"></i> Flagship</span>
 </div>
 <div>
 <h3 className="home-products__title home-products__title--lg">Informed Intake</h3>
 <p className="home-products__sub">by Moneli Automation Inc.</p>
 </div>
 <p className="home-products__desc">
 24/7 digital receptionist built on Microsoft Power Platform. Automates calls, SMS, chatbots,
 and visitor intake for clinics, property managers, and public agencies.
 </p>
 <div className="home-products__foot">
 <a className="btn btn-primary" href="https://informedintake.com" target="_blank" rel="noopener noreferrer" data-magnetic>
 Visit Site <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
 </a>
 </div>
 </div>
 <ul className="ticks home-products__ticks">
 {['AI-powered voice assistant', 'Intelligent appointment scheduling', 'SMS and chat integration', 'Visitor management system', 'Multi-language support'].map((f) => (
 <li key={f}>{f}</li>
 ))}
 </ul>
 </article>

 {PRODUCTS.map((p) => (
 <article className="card home-products__card" key={p.title}>
 <div className="home-products__row">
 <span className="icon-tile icon-tile--sm"><i className={`fa-solid ${p.icon}`} aria-hidden="true"></i></span>
 <div>
 <h3 className="home-products__title">{p.title}</h3>
 <p className="home-products__sub">{p.subtitle}</p>
 </div>
 </div>
 <p className="home-products__desc">{p.desc}</p>
 <div className="home-products__foot">
 {p.external ? (
 <a className="link-arrow" href={p.link} target="_blank" rel="noopener noreferrer">
 {p.linkText}<span className="visually-hidden">: {p.title}</span> <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
 </a>
 ) : (
 <Link className="link-arrow" to={p.link}>
 {p.linkText}<span className="visually-hidden">: {p.title}</span> <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
 </Link>
 )}
 </div>
 </article>
 ))}
 </div>
 </div>
 </section>
 </>
 );
}

export default Home;
